// Auto-generated from docs/architecture/flux-phd.md — do not edit manually
const content = `
# Flux PhD Architecture

Flux PhD is the advanced analytics, research, and AI domain within the Vortx Flux platform. It provides physics-based topology simulation, component-level load forecasting, coordination policy training, and a built-in knowledge base (wiki).

## Overview

Flux PhD serves four primary functions:

1. **AC/DC Topology Analysis** -- Physics-based simulation of 12 electrical topology presets (AC-coupled, DC-coupled, hybrid) for solar + storage + EV charging systems. Calculates minute-by-minute energy flows, conversion losses, and path efficiencies using Sandia inverter models and C-rate-dependent battery models.

2. **Component Forecasting** -- Cold-start load prediction pipeline built on DOE ComStock data. Trains LightGBM component models (HVAC, refrigeration, plug loads, lighting) clustered by building archetype, then produces 8760-hour load profiles from weather data and building metadata alone. Consumed by Flux EMS for sites without historical meter data.

3. **Coordination Policy** -- Adaptive meta-controller training pipeline for multi-agent ADMM coordination. Generates benchmark scenarios from ComStock buildings, runs coordination sweeps across tariff/EV/cold-storage combinations, and trains a LightGBM policy model (Tier 3) that predicts optimal coordination parameters (DC fraction, ADMM iterations, rho, alpha, convergence tolerance) from site features.

4. **Wiki / Knowledge Base** -- In-app documentation covering PhD simulation guides, forecasting pipeline docs, ADMM/PoP concepts, and system architecture references. Rendered from JS-exported markdown pages with a table-of-contents sidebar.

## Architecture

\`\`\`
                                  Frontend
                                  --------
                          flux-phd-web (React, MUI)
                                    |
                    +---------+-----+------+---------+
                    |         |            |         |
               AC/DC    Forecasting   Research    Wiki
              Systems     Section     Section   Section
                    |         |            |
                    v         v            v
                          PhD API
                          -------
                   flux-phd-api (Flask, Cloud Run)
                                    |
            +--------+---------+----+------+--------+
            |        |         |          |         |
       simulation  routes   services  flux_core   tests
            |        |         |          |
      topologies  10 blueprints  service    coordination/
      engine      (sim, report,  layer     forecasting/
      efficiency   coordination,           simulation/
      equipment    validation,
                   batch, etc.)
\`\`\`

### Request Flow

The frontend communicates with two backends:

- **PhD API** (\`REACT_APP_PHD_API_URL\`) -- Direct calls for topology simulation, forecasting, coordination, and reports. Most simulation endpoints do not require authentication.
- **Platform API** (\`REACT_APP_API_URL\`) -- Authenticated calls for PhD efficiency profiles stored in Site DNA (proxied through the main platform gateway).

\`\`\`
flux-phd-web
    |
    +-- phdService.js ----> PhD API (flux-phd-api, Cloud Run, port 8080)
    |                           /api/topologies, /api/simulation, /api/simulations,
    |                           /api/coordination/*, /api/reports/*
    |
    +-- forecastingService.js -> PhD API
    |                           /api/comstock/*, /api/sampling/*, /api/models/*,
    |                           /api/validation/*
    |
    +-- api.js (shared) -----> Platform API (vortx-platform-api)
                                /api/site_dna/v1/flux-phd/profiles
\`\`\`

### Deployment

- **Backend**: Cloud Run service \`flux-phd-api\` deployed via \`tools/deploy-phd.sh\`
- **Frontend**: Workspace package \`flux-phd-web\` compiled into the \`flux-web\` shell via craco, deployed with \`tools/deploy-frontend.sh\`
- **Docker**: Python 3.11-slim base with weasyprint dependencies for PDF generation, gunicorn (2 workers, 4 threads, 300s timeout)
- **Benchmark Runner**: Separate Cloud Run Job \`flux-benchmark-runner\` triggered by the coordination service via gcloud CLI

## API Routes

All routes are registered under the \`/api\` URL prefix.

### Topology & Simulation (\`simulation_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| GET | \`/api/topologies\` | No | List all 12 topology presets |
| GET | \`/api/topologies/<id>\` | No | Get topology detail (efficiencies, diagram, bus voltages) |
| POST | \`/api/simulation\` | No | Run minute-by-minute simulation (topology preset or custom) |
| POST | \`/api/simulation/compare\` | No | Compare multiple topologies side-by-side |
| GET | \`/api/simulation/<id>\` | No | Get async simulation status/results |
| GET | \`/api/simulation/<id>/timeseries\` | No | Get timeseries data slice |
| GET | \`/api/simulations\` | Yes | List user's saved simulations |
| GET | \`/api/simulations/<id>\` | Yes | Get saved simulation with full results |
| POST | \`/api/simulations\` | Yes | Save simulation results to Firestore + Cloud Storage |
| DELETE | \`/api/simulations/<id>\` | Yes | Delete saved simulation |
| POST | \`/api/simulations/<id>/export-profile\` | Yes | Export as Site DNA efficiency profile |

### Reports (\`report_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/reports/efficiency-comparison\` | No | Generate efficiency comparison report |
| GET | \`/api/reports/<id>\` | No | Get report status/download link |
| POST | \`/api/reports/white-paper\` | No | Generate white paper PDF (returns file download) |
| POST | \`/api/reports/white-paper/preview\` | No | Generate HTML preview of white paper |
| POST | \`/api/reports/sankey\` | No | Generate Sankey diagram as PNG or SVG |
| POST | \`/api/reports/sankey/data\` | No | Get Sankey data structure for client-side Plotly rendering |

### EV Profile Generation (\`ev_profile_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| GET | \`/api/ev-profile/use-cases\` | No | List EV use case presets (workplace, overnight, midday, public DCFC) |
| POST | \`/api/ev-profile/generate\` | No | Generate minute-resolution EV charging load profile |
| POST | \`/api/ev-profile/library/generate\` | No | Generate parametric EV profile library (216 scenarios) |
| GET | \`/api/ev-profile/library/status\` | No | Library generation job status |
| GET | \`/api/ev-profile/library\` | No | List library scenarios with metadata filters |
| GET | \`/api/ev-profile/library/<id>\` | No | Get single scenario config + 35,040-step profile |
| POST | \`/api/ev-profile/pairings/build\` | No | Generate building-type to EV scenario pairing index |
| GET | \`/api/ev-profile/pairings\` | No | Get current building-to-EV pairing index |

### ComStock Data Pipeline (\`comstock_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/comstock/download\` | No | Start ComStock data download for a building type |
| GET | \`/api/comstock/status\` | No | Get download job status |
| GET | \`/api/comstock/buildings\` | No | List buildings with metadata filters (type, climate zone, vintage, sqft) |
| GET | \`/api/comstock/buildings/<id>\` | No | Get building metadata summary |
| GET | \`/api/comstock/buildings/<id>/profile\` | No | Get full component-decomposed 8760 profile |
| GET | \`/api/comstock/stats\` | No | Aggregate statistics for cached ComStock data |

### Stratified Sampling (\`sampling_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/sampling/build\` | No | Start stratified sampling job (building_type x climate_zone cells) |
| GET | \`/api/sampling/status\` | No | Get sampling job status |
| GET | \`/api/sampling/coverage\` | No | Get cell fill rates and coverage heatmap data |
| GET | \`/api/sampling/buildings\` | No | List sampled buildings with cell assignments |
| GET | \`/api/sampling/stats\` | No | Per-cell sampling statistics |

### Model Training & Registry (\`model_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/models/train\` | No | Start model training job (HVAC, refrigeration, plug loads, lighting) |
| GET | \`/api/models/train/<job_id>\` | No | Get training job status and progress |
| GET | \`/api/models\` | No | List all model versions with metrics |
| GET | \`/api/models/active\` | No | Get currently active model version |
| GET | \`/api/models/<version>\` | No | Get model version details (manifest, per-component metrics) |
| GET | \`/api/models/<version>/<component>/<cluster>\` | No | Get specific component model metrics + feature importances |
| POST | \`/api/models/<version>/activate\` | No | Set model version as active production model |
| POST | \`/api/models/compare\` | No | Compare two model versions side-by-side |
| POST | \`/api/models/active/predict\` | No | Run cold-start load forecast using active model (primary EMS endpoint) |
| POST | \`/api/models/<version>/predict\` | No | Run forecast with specific model version |
| POST | \`/api/models/active/fine-tune\` | No | Submit real meter data for model fine-tuning |
| GET | \`/api/models/active/fine-tune/<site_id>\` | No | Get fine-tuning adaptation status for a site |

### Validation (\`validation_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/validation/run\` | No | Start full validation suite run |
| GET | \`/api/validation\` | No | List all validation runs |
| GET | \`/api/validation/<run_id>\` | No | Get validation run status and results |
| POST | \`/api/validation/cold-start\` | No | Start quick cold-start validation test |

### Coordination Policy (\`coordination_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/coordination/generate-scenarios\` | No | Generate benchmark scenarios from synthetic buildings |
| POST | \`/api/coordination/run-benchmark\` | No | Submit cloud benchmark run (Cloud Run Jobs + Firestore) |
| GET | \`/api/coordination/benchmark-runs\` | No | List benchmark runs (ordered by submitted_at desc) |
| GET | \`/api/coordination/benchmark-runs/<run_id>\` | No | Get benchmark run detail with scenario sub-collection |
| POST | \`/api/coordination/train-policy\` | No | Train LightGBM policy model from benchmark results |
| GET | \`/api/coordination/benchmark-results\` | No | Get most recent local benchmark results |
| GET | \`/api/coordination/policy-model/status\` | No | Check if policy model is trained |
| GET | \`/api/coordination/jobs\` | No | List all async jobs (generate, benchmark, train) |
| GET | \`/api/coordination/jobs/<job_id>\` | No | Get specific job status |

### Batch ADMM Simulation (\`batch_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| POST | \`/api/batch/run\` | No | Start batch ADMM simulation sweep |
| GET | \`/api/batch\` | No | List all batch runs |
| GET | \`/api/batch/<batch_id>\` | No | Get batch job status and progress |
| GET | \`/api/batch/<batch_id>/results\` | No | Aggregate results (filterable by tariff, system, coordination mode) |
| GET | \`/api/batch/<batch_id>/results/<building_id>\` | No | Per-building results |
| GET | \`/api/batch/<batch_id>/analysis\` | No | Pre-computed analysis (PoP distributions by tariff, system) |

### Digital Twin (\`twin_routes.py\`)

| Method | Endpoint | Auth | Description |
|--------|----------|------|-------------|
| GET | \`/api/twin/projects/<project_id>/scenarios\` | Header | Cross-app scenario aggregation (Site DNA + Flux EV + Flux COLD) |

Uses \`X-User-ID\` header injected by Platform API for user identification.

## Frontend Components

### Main App Structure (\`flux-phd-web/src/\`)

\`\`\`
pages/
  FluxPhdApp.jsx          -- Main app entry, section router (ac_dc_systems, forecasting, wiki, research)
  AppRoot.jsx              -- Root wrapper with routing
  ProjectsPage.jsx         -- Project listing

components/phd/
  AcDcSystemsTab.jsx       -- Combined Topology -> Simulation -> Results workflow
  TopologyBrowser.jsx      -- Browse and select from 12 topology presets
  TopologyDetail.jsx       -- Detailed topology view with diagram and efficiencies
  SimulationConfig.jsx     -- Configure simulation parameters (equipment, profiles, duration)
  SimulationProgress.jsx   -- Progress indicator during simulation
  ResultsView.jsx          -- Single simulation results display
  ComparisonView.jsx       -- Side-by-side topology comparison results

  forecasting/
    ForecastingSection.jsx    -- Tab container: Data | Training | Validation | Forecast
    DataPipelineTab.jsx       -- ComStock download management and sampling
    TrainingTab.jsx           -- Model training controls and status
    ValidationTab.jsx         -- Validation suite runner and results
    ForecastTab.jsx           -- Cold-start prediction interface
    ComponentAccuracyChart.jsx -- Per-component accuracy visualization
    CriterionCard.jsx         -- Validation criterion display card
    ScenarioPopChart.jsx      -- PoP distribution chart by scenario
    GoNogoBanner.jsx          -- Go/no-go validation status banner

  research/
    ResearchSection.jsx       -- Cloud benchmark runner UI
    BenchmarkRunsTable.jsx    -- Table listing benchmark runs with status
    BenchmarkRunDetail.jsx    -- Detail view for a single benchmark run with scenario results

  results/
    ComparisonDashboard.jsx   -- Multi-topology comparison dashboard
    EnergyFlowChart.jsx       -- Energy flow visualization
    HistoryCard.jsx           -- Saved simulation history card
    KeyFindingsCard.jsx       -- Key findings summary card
    MethodologyTooltip.jsx    -- Hover tooltip for methodology details
    MetricPills.jsx           -- Compact metric display pills
    ResultsDesignTokens.jsx   -- Shared design tokens (colors, animations)
    SankeyDiagram.jsx         -- Client-side Sankey energy flow diagram
    SingleLineDiagram.jsx     -- Electrical single-line diagram

  wiki/
    WikiSection.jsx           -- Two-column wiki layout (TOC + content)
    WikiToc.jsx               -- Table of contents sidebar navigation
    WikiContent.jsx           -- Markdown renderer for wiki pages

  utils/
    profileGenerators.js      -- Synthetic load/solar profile generators for simulation config
    topologyColors.jsx        -- Topology-specific accent colors
\`\`\`

### Hooks (\`flux-phd-web/src/hooks/\`)

| Hook | Purpose |
|------|---------|
| \`usePhdTopologies\` | Fetch and manage topology preset data |
| \`usePhdSimulation\` | Run simulations and track results |
| \`usePhdHistory\` | Manage saved simulation history (CRUD) |
| \`usePhdProjects\` | List user's projects |
| \`usePhdProject\` | Load single project data |
| \`useForecastingService\` | ComStock data, sampling, training, validation state |
| \`useSimulationWebSocket\` | WebSocket connection for real-time simulation updates |
| \`useSimulationStatus\` | Poll simulation status + kill capability |
| \`useTwinScenarios\` | Fetch cross-app scenarios for Digital Twin |
| \`useBenchmarkRuns\` | Firestore listener for benchmark run list |
| \`useBenchmarkRunDetail\` | Firestore listener for benchmark run detail + scenarios |
| \`useSkeletonState\` | Loading skeleton management |

### Wiki System (\`flux-phd-web/src/wiki/\`)

The wiki is a static knowledge base rendered from JS-exported markdown strings. Pages are organized into four sections:

| Section | Pages |
|---------|-------|
| **PhD Guide** | Overview, Topologies, Simulation, Results, Comparison |
| **Forecasting** | Overview, Data Pipeline, Model Training, Validation, Cold-Start Prediction |
| **Concepts** | Glossary, Peak Optimization (PoP), ADMM Coordination, Component Models |
| **Architecture** | System Overview, PhD API, Solver |

Pages are imported eagerly (no lazy loading) since they are small string exports. The \`manifest.js\` file defines \`WIKI_SECTIONS\` (table of contents structure), \`getPageContent()\` (page lookup), and \`DEFAULT_PAGE_ID\`.

## Coordination Policy Pipeline

The coordination policy pipeline trains an ML model that predicts optimal ADMM coordination parameters for a given site profile. This is the Tier 3 layer of the adaptive meta-controller.

### Pipeline Stages

\`\`\`
1. Generate Scenarios
   ScenarioGenerator builds scenarios from ComStock buildings x EV presets x tariff variants
       |
2. Run Benchmarks
   Cloud benchmark runs (Cloud Run Jobs) evaluate scenarios via siloed/orchestrated/global-optimum
       |
3. Train Policy Model
   LightGBM multi-output regression learns site features -> coordination parameters
       |
4. Deploy

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
